import type { PendingPromotionResponse, Promotion } from '@promotion/types'

export const useRewardChecks = () => {
    const rewardTypes = {
            freeSpins: 'FreeSpinsConfiguration',
            scratchCard: 'ScratchCardConfiguration',
            wheelOfFortune: 'WheelOfFortuneConfiguration',
            bingoTicket: 'BingoFreeTicketConfiguration',
            bundle: 'Bundle',
            bonus: 'Bonus',
            bonusPromotion: 'NewBonus',
        },
        triggerTypes = {
            signup: 5,
            login: 2,
            streak: 12,
        },
        REQUIREMENT_TYPE_PAYMENT = 'Payment',
        REQUIREMENT_TYPE_SPEND = 'Spend',
        STATUS_PENDING_REQUIREMENT = 'Pending requirement',
        BONUS_STATUS_PENDING_TERMS_ACCEPTED = 'PendingTermsAccepted',

        STATUS_ACTIVE = 'Active',
        STATUS_PENDING = 'Pending',
        STATUS_UNDECIDED = 'Undecided',

        isRewardType = (reward: Promotion, type: string) => {
            return reward.type === type || reward.foreign_model === type || type in reward
        },

        hasTurnOverMultiplier = (reward: Promotion) => Number(reward.turnover_multiplier) > 0,

        isScratchCards = (promotion: Promotion) => isRewardType(promotion, rewardTypes.scratchCard),

        isSignupBonusSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.freeSpins)
            && Number(promotion.trigger_type) === triggerTypes.signup
            && promotion.requirement_type === REQUIREMENT_TYPE_PAYMENT,

        isBonusSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.freeSpins),

        isBundle = (promotion: Promotion) => isRewardType(promotion, rewardTypes.bundle),

        isBonus = (promotion: Promotion) => isRewardType(promotion, rewardTypes.bonus),

        isWheelOfFortuneSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.wheelOfFortune),

        isBingoTickets = (promotion: Promotion) => isRewardType(promotion, rewardTypes.bingoTicket),

        isBonusCash = (promotion: Promotion) => {
            if (isBonus(promotion) && Number(promotion.amount)) return true
            // until we restructure promotion-naming to not include 'NewBonus' - this is the way.
            if (isRewardType(promotion, rewardTypes.bonusPromotion) && Number(promotion.NewBonus?.amount)) return true

            return false
        },

        isValidForeignModel = (foreignModel: string) => {
            return foreignModel === rewardTypes.freeSpins || rewardTypes.scratchCard || rewardTypes.wheelOfFortune || rewardTypes.bingoTicket || rewardTypes.bonus || rewardTypes.bonusPromotion
        },

        isBonusPendingPaymentRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_PAYMENT
            && reward.bonusOfferStatus === BONUS_STATUS_PENDING_TERMS_ACCEPTED,

        isPromotionPendingPaymentRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_PAYMENT && reward.status === STATUS_PENDING_REQUIREMENT,

        isPromotionPendingSpendRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_SPEND && reward.status === STATUS_PENDING_REQUIREMENT,

        isBundlePendingPaymentRequirement = (reward: Promotion) => isBundle(reward) && reward.requirement_type === REQUIREMENT_TYPE_PAYMENT,

        isPaymentPendingRequirement = (reward: Promotion): boolean => {
            if (isBundle(reward)) {
                return isBundlePendingPaymentRequirement(reward)
            }
            if (isBonus(reward)) {
                return isBonusPendingPaymentRequirement(reward)
            }
            return isPromotionPendingPaymentRequirement(reward)
        },

        showTurnOverMultiplier = (reward: Promotion): boolean => {
            // Only show progress, if the turnover is larger than 1 for bingo tickets and bonus spins
            if (isBonusSpins(reward) || isBingoTickets(reward)) {
                return Number(reward.turnover_multiplier) > 1
            }

            return hasTurnOverMultiplier(reward)
        }

    /**
     * Checks if the given object is a Promotion.
     * @param obj - The object to check.
     * @returns True if the object is a Promotion, false otherwise.
     */
    function isPromotion(obj: any): obj is Promotion {
        return (
            obj
            && typeof obj === 'object'
            && typeof obj.external_id === 'string'
            && (typeof obj.requirement_type === 'string' || obj.requirement_type === null)
        )
    }

    /**
     * Checks if the given object is a PendingPromotionResponse.
     * @param obj - The object to check.
     * @returns True if the object is a PendingPromotionResponse, false otherwise.
     */
    function isPendingPromotion(
        obj: any
    ): obj is PendingPromotionResponse {
        return (
            obj
            && typeof obj === 'object'
            && typeof obj.title === 'string'
            && 'Promotion' in obj
            && isPromotion(obj.Promotion) // Reusing the type guard for the nested object
        )
    }

    return {
        rewardTypes,
        triggerTypes,
        REQUIREMENT_TYPE_PAYMENT,
        REQUIREMENT_TYPE_SPEND,
        STATUS_PENDING_REQUIREMENT,
        BONUS_STATUS_PENDING_TERMS_ACCEPTED,
        STATUS_ACTIVE,
        STATUS_PENDING,
        STATUS_UNDECIDED,
        isBundle,
        isBonus,
        isBonusCash,
        isBonusSpins,
        isBingoTickets,
        isScratchCards,
        isSignupBonusSpins,
        isValidForeignModel,
        isWheelOfFortuneSpins,
        showTurnOverMultiplier,
        hasTurnOverMultiplier,
        isBonusPendingPaymentRequirement,
        isPromotionPendingPaymentRequirement,
        isPromotionPendingSpendRequirement,
        isBundlePendingPaymentRequirement,
        isPaymentPendingRequirement,
        isPromotion,
        isPendingPromotion
    }
}
